import { canUseAdminTenantScope } from '@/utils/authz';

/**
 * Builds the API path segment based on role scope:
 * - admin-equivalent → `/<segment>`
 * - non-admin → `/client/<segment>`
 *
 * Note: appointments use a separate customer branch via {@link roleAppointmentsBase}.
 */
export const rolePath = (
  role: string | undefined,
  segment: string,
  clientPrefix: string = 'client'
) => (canUseAdminTenantScope(role) ? segment : `${clientPrefix}/${segment}`);


/** Injects selected admin client into query/body params. Non-admin roles pass params through unchanged. */
export const withAdminClientParam = <T extends { clientId?: string } | undefined>(
  role: string | undefined,
  params: T,
  selectedClientId?: string | null
) => {
  if (!canUseAdminTenantScope(role)) return params;
  const current = (params || {}) as { clientId?: string };
  return { ...current, clientId: selectedClientId || current.clientId } as T;
};

/**
 * Injects selected admin company into params, or removes companyId when
 * "all companies" is selected.
 */
export const withAdminCompanyParam = <T extends { companyId?: string | null } | undefined>(
  role: string | undefined,
  params: T,
  selectedCompanyId?: string | null
) => {
  if (!canUseAdminTenantScope(role)) return params;
  const current = (params || {}) as Record<string, unknown>;
  if (selectedCompanyId) {
    return { ...current, companyId: selectedCompanyId } as T;
  }
  const { companyId: _omit, ...rest } = current;
  return rest as T;
};

/** Stable role-aware query key segments for React Query cache separation. */
export const queryRoleKey = (
  role: string | undefined,
  selectedClientId?: string | null,
  selectedCompanyId?: string | null
): (string | undefined)[] =>
  canUseAdminTenantScope(role)
    ? ['admin', selectedClientId || 'all', selectedCompanyId || 'all']
    : role === 'CUSTOMER'
      ? ['customer']
      : ['client'];

/**
 * Returns true when a role-scoped query or mutation may run.
 * Gates on auth loading, authentication, and (for admins) client selector readiness.
 */
export const isRoleQueryReady = (
  role: string | undefined,
  loading: boolean,
  isSelectionReady?: boolean,
  selectedClientId?: string | null,
  isAuthenticated: boolean = true
): boolean => {
  if (loading || !isAuthenticated) return false;
  if (canUseAdminTenantScope(role)) {
    return !!isSelectionReady && !!selectedClientId;
  }
  return true;
};

/** Scope identifiers used when injecting admin client/company into params or bodies. */
export type AdminScopeIds = {
  clientId?: string | null;
  companyId?: string | null;
};

/**
 * Applies admin client and company injection to query/body params in one step.
 * Non-admin roles receive params unchanged.
 *
 * @param role - Current user role
 * @param params - Original params object
 * @param scope - Selected admin client and company IDs
 * @returns Params with admin scope injected when applicable
 */
export const withAdminScopeParams = <T extends { clientId?: string; companyId?: string | null } | undefined>(
  role: string | undefined,
  params: T,
  scope: AdminScopeIds
): T => {
  const withClient = withAdminClientParam(role, params, scope.clientId);
  return withAdminCompanyParam(role, withClient, scope.companyId);
};

/**
 * Builds a mutation request body with admin clientId and optional companyId injected.
 * Non-admin roles receive the body unchanged.
 *
 * @param role - Current user role
 * @param body - Original request body
 * @param effectiveClientId - Resolved client ID for admin scope
 * @param effectiveCompanyId - Resolved company ID for admin scope (optional)
 * @returns Body with admin scope fields when applicable
 */
export const buildAdminMutationBody = <T extends { clientId?: string; companyId?: string | null }>(
  role: string | undefined,
  body: T,
  effectiveClientId: string | null | undefined,
  effectiveCompanyId?: string | null
): T => {
  if (!canUseAdminTenantScope(role)) return body;
  const companyId = body.companyId ?? effectiveCompanyId ?? undefined;
  return {
    ...body,
    clientId: effectiveClientId as string,
    ...(companyId ? { companyId } : {})
  };
};
